import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { MessageSquare, Send, Users, AlertTriangle, History, BarChart3 } from "lucide-react";
import { PageHeader, SectionCard, EmptyState, StatCard } from "../components/ui/kaizen";

const AUDIENCES = [
  { id: "active", label: "Membres actifs" },
  { id: "inactive", label: "Anciens membres" },
  { id: "essai", label: "En essai" },
  { id: "all", label: "Tous les contacts" },
  { id: "custom", label: "Liste précise" },
];

const TABS = [
  { id: "compose", label: "Composer", icon: Send },
  { id: "dashboard", label: "Historique", icon: BarChart3 },
];

export default function SmsBroadcastModule() {
  const [tab, setTab] = useState("compose");
  const [audience, setAudience] = useState("active");
  const [discipline, setDiscipline] = useState("all");
  const [customPhones, setCustomPhones] = useState("");
  const [message, setMessage] = useState("");
  const [preview, setPreview] = useState(null);
  const [disciplines, setDisciplines] = useState([]);
  const [dash, setDash] = useState(null);
  const [busy, setBusy] = useState(false);
  const [testPhone, setTestPhone] = useState("");

  const parseCustom = () => customPhones.split(/[\s,;]+/).map((p) => p.trim()).filter(Boolean);

  const loadPreview = async (aud = audience, disc = discipline, msg = message) => {
    try {
      const { data } = await api.post("/sms-broadcast/preview", {
        audience: aud, discipline: disc === "all" ? null : disc,
        custom_phones: aud === "custom" ? parseCustom() : [], message: msg,
      });
      setPreview(data);
    } catch (e) { toast.error("Erreur d'aperçu de l'audience."); }
  };
  const loadDisciplines = async () => {
    try { const { data } = await api.get("/sms-broadcast/disciplines"); setDisciplines(data.disciplines || []); } catch (e) {}
  };
  const loadDashboard = async () => {
    try { const { data } = await api.get("/sms-broadcast/dashboard"); setDash(data); } catch (e) {}
  };

  useEffect(() => { loadPreview("active", "all", ""); loadDisciplines(); loadDashboard(); }, []);
  useEffect(() => {
    const raw = localStorage.getItem("prefill_sms");
    if (raw) {
      try {
        const p = JSON.parse(raw);
        setMessage(p.message || "");
        toast.success("SMS pré-rempli depuis la campagne « Les 3 ».");
      } catch (e) {}
      localStorage.removeItem("prefill_sms");
    }
  }, []);
  useEffect(() => { if (audience !== "custom") loadPreview(audience, discipline, message); }, [audience, discipline]); // eslint-disable-line
  useEffect(() => { const t = setTimeout(() => loadPreview(audience, discipline, message), 400); return () => clearTimeout(t); }, [message]); // eslint-disable-line

  const seg = preview?.segments || { length: message.length, segments: 1, unicode: false };

  const send = async () => {
    if (!message.trim()) { toast.error("Message requis."); return; }
    const count = preview?.total || 0;
    if (!count) { toast.error("Aucun destinataire."); return; }
    if (!window.confirm(`Envoyer ce SMS à ${count} destinataire(s) ? (~${seg.segments} segment(s) chacun). Action irréversible.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/sms-broadcast/send?confirm=true", {
        audience, discipline: discipline === "all" ? null : discipline,
        custom_phones: audience === "custom" ? parseCustom() : [], message: message.trim(),
      });
      toast.success(`Envoyé : ${data.sent} · Échecs : ${data.failed}`);
      if (data.failed) toast.warning(`${data.failed} échec(s). Voir l'historique.`);
      setMessage(""); loadDashboard();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'envoi.");
    } finally { setBusy(false); }
  };

  const sendTest = async () => {
    if (!message.trim()) { toast.error("Message requis."); return; }
    if (!testPhone.trim()) { toast.error("Entre ton numéro pour le test."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/sms-broadcast/test", { phone: testPhone.trim(), message: message.trim() });
      toast.success(`SMS test envoyé à ${data.sent_to}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'envoi test.");
    } finally { setBusy(false); }
  };

  const inputCls = "w-full mt-1.5 px-3 py-2 border border-zinc-700 rounded-lg text-sm";

  return (
    <div className="space-y-6" data-testid="sms-broadcast-module">
      <PageHeader title="📱 Diffusion SMS (à tous)" subtitle="Envoi de SMS en masse via Twilio · audiences, aperçu du coût & historique." icon={MessageSquare} />

      {preview && !preview.twilio_configured && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm" data-testid="twilio-warning">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <span>Twilio n'est pas configuré (clés manquantes). L'aperçu fonctionne, mais l'envoi est désactivé.</span>
        </div>
      )}

      <div className="flex flex-wrap gap-2 border-b border-zinc-800 pb-1">
        {TABS.map((t) => (
          <button key={t.id} data-testid={`sms-tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-t-lg text-sm font-semibold transition-colors ${
              tab === t.id ? "bg-[#D90429] text-white" : "bg-transparent text-zinc-400 hover:bg-zinc-800"
            }`}>
            <t.icon size={15} /> {t.label}
          </button>
        ))}
      </div>

      {tab === "compose" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <SectionCard title="Composer le SMS" icon={Send}>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-semibold text-zinc-300">Audience</label>
                  <div className="flex flex-wrap gap-2 mt-1.5">
                    {AUDIENCES.map((a) => (
                      <button key={a.id} data-testid={`sms-audience-${a.id}`} onClick={() => setAudience(a.id)}
                        className={`px-3 py-2 rounded-lg text-sm font-semibold border transition-colors ${
                          audience === a.id ? "bg-[#D90429] text-white border-[#D90429]" : "bg-zinc-800 text-zinc-300 border-zinc-800 hover:bg-zinc-800/60"
                        }`}>
                        {a.label}
                      </button>
                    ))}
                  </div>
                </div>

                {audience !== "custom" && (
                  <div>
                    <label className="text-sm font-semibold text-zinc-300">Discipline</label>
                    <select data-testid="sms-discipline-select" value={discipline} onChange={(e) => setDiscipline(e.target.value)} className={inputCls}>
                      <option value="all">Toutes les disciplines</option>
                      {disciplines.map((d) => <option key={d.id} value={d.id}>{d.label} ({d.count})</option>)}
                    </select>
                  </div>
                )}

                {audience === "custom" && (
                  <div>
                    <label className="text-sm font-semibold text-zinc-300">Numéros (virgule / espace / ligne)</label>
                    <textarea data-testid="sms-custom-input" value={customPhones} onChange={(e) => setCustomPhones(e.target.value)}
                      onBlur={() => loadPreview("custom")} rows={3} className={inputCls} placeholder="819-555-1234, 8195551235" />
                  </div>
                )}

                <div>
                  <label className="text-sm font-semibold text-zinc-300">Message</label>
                  <textarea data-testid="sms-message-input" value={message} onChange={(e) => setMessage(e.target.value)} rows={6} className={inputCls}
                    placeholder="Ex. : Institut Impact - Horaire d'ete 2026: dojo ouvert tout l'ete..." />
                  <div className="flex items-center justify-between mt-1 text-xs">
                    <span className="text-zinc-400" data-testid="sms-char-count">{seg.length} caractères · ~{seg.segments} segment(s)/SMS</span>
                    {seg.unicode && <span className="text-amber-600 font-semibold">⚠️ Caractère spécial/emoji détecté → encodage Unicode (70 car./segment, plus coûteux)</span>}
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  <button data-testid="sms-send-btn" onClick={send} disabled={busy || !preview?.twilio_configured}
                    className="flex items-center gap-2 bg-[#D90429] hover:bg-[#b8031f] text-white font-semibold px-5 py-2.5 rounded-lg disabled:opacity-50">
                    <Send size={16} /> {busy ? "Envoi en cours…" : `Envoyer à ${preview?.total ?? 0} destinataire(s)`}
                  </button>
                </div>

                <div className="rounded-lg border border-zinc-800 p-3 bg-zinc-800/60">
                  <label className="text-sm font-semibold text-zinc-300">Test à un numéro</label>
                  <div className="flex gap-2 mt-1.5">
                    <input data-testid="sms-test-phone" value={testPhone} onChange={(e) => setTestPhone(e.target.value)}
                      className="flex-1 px-3 py-2 border border-zinc-700 rounded-lg text-sm" placeholder="Ton numéro (ex. 819-527-9446)" />
                    <button data-testid="sms-test-btn" onClick={sendTest} disabled={busy || !preview?.twilio_configured}
                      className="bg-zinc-800 border border-zinc-700 hover:bg-zinc-800/60 text-zinc-200 font-semibold px-4 py-2 rounded-lg disabled:opacity-50">
                      Tester
                    </button>
                  </div>
                </div>
              </div>
            </SectionCard>
          </div>

          <div className="space-y-6">
            <SectionCard title="Audience ciblée" icon={Users}>
              <div className="text-center py-2">
                <p className="text-4xl font-black text-zinc-100" data-testid="sms-audience-count">{preview?.total ?? "—"}</p>
                <p className="text-xs text-zinc-400">destinataires (avec numéro valide)</p>
              </div>
              <p className="text-xs text-zinc-400 mt-2">Expéditeur : <strong>{preview?.from_number || "—"}</strong></p>
              {preview?.sample?.length > 0 && (
                <div className="mt-3 text-xs text-zinc-400 space-y-0.5">
                  {preview.sample.map((p) => <p key={p} className="truncate">• {p}</p>)}
                  {preview.total > preview.sample.length && <p className="text-zinc-500">+ {preview.total - preview.sample.length} autres…</p>}
                </div>
              )}
            </SectionCard>
          </div>
        </div>
      )}

      {tab === "dashboard" && (
        <div className="space-y-6" data-testid="sms-dashboard">
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            <StatCard label="Campagnes SMS" value={dash?.totals?.campaigns ?? 0} icon={History} />
            <StatCard label="SMS envoyés" value={dash?.totals?.sent ?? 0} icon={Send} />
            <StatCard label="Échecs" value={dash?.totals?.failed ?? 0} icon={AlertTriangle} />
          </div>
          <SectionCard title="Historique des campagnes SMS" icon={History}>
            {!dash || dash.campaigns.length === 0 ? (
              <EmptyState icon={History} title="Aucune campagne SMS" description="Tes envois SMS apparaîtront ici." />
            ) : (
              <div className="overflow-x-auto" data-testid="sms-campaigns-table">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-zinc-400 border-b border-zinc-800">
                      <th className="py-2 pr-4 font-semibold">Message</th>
                      <th className="py-2 px-2 font-semibold">Audience</th>
                      <th className="py-2 px-2 font-semibold text-right">Envoyés</th>
                      <th className="py-2 px-2 font-semibold text-right">Segments</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dash.campaigns.map((c) => (
                      <tr key={c.id} data-testid={`sms-campaign-row-${c.id}`} className="border-b border-zinc-800">
                        <td className="py-2 pr-4"><p className="text-zinc-200 truncate max-w-[320px]">{c.message}</p></td>
                        <td className="py-2 px-2 text-zinc-400">{c.audience_label}{c.discipline_label ? ` · ${c.discipline_label}` : ""}</td>
                        <td className="py-2 px-2 text-right font-semibold text-zinc-200">{c.sent}{c.failed > 0 && <span className="text-[#D90429]"> ({c.failed} éch.)</span>}</td>
                        <td className="py-2 px-2 text-right text-zinc-400">{c.segments_each}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </SectionCard>
        </div>
      )}
    </div>
  );
}
